/**
 * «È in diretta» lo decide l'app.
 *
 * Non esiste un booleano `live` che arriva dal backend, e non deve esistere: una chiave
 * Redis che si riscrive a ogni raccolta conterrebbe un verdetto vecchio di ore
 * (05-CONTRATTO-API §6.1, 12-BACKEND-E-GATEWAY §6). Al player arrivano gli orari — che
 * non invecchiano — e `now`, l'istante del server.
 *
 * Per un evento sportivo gli orari sono due coppie, e non sono intercambiabili
 * (05-CONTRATTO-API §6.2):
 *
 *  - `start`/`end` vengono dallo scraper, e `end` è **stimato** (`start + 105 minuti`,
 *    una costante). Una partita ai supplementari risulterebbe finita mentre si gioca.
 *  - `epgStart`/`epgEnd` vengono dalla guida. Sono vuoti quando la guida non ha trovato
 *    il programma — e sono vuoti sul serio, non copiati dagli altri due.
 *
 * Il verdetto si dà sulla guida quando c'è. Gli orari dello scraper servono a collocare
 * un evento in «più tardi in TV», non a dire che è finito.
 */

export type Liveness = 'past' | 'live' | 'future';

/** Da dove viene la finestra su cui il verdetto è stato dato. */
export type LivenessSource = 'epg' | 'estimated';

export interface LivenessVerdict {
  readonly state: Liveness;
  readonly source: LivenessSource;
}

export interface TimeWindow {
  readonly startMs: number;
  readonly endMs: number;
}

/** Gli orari di un evento come arrivano da `GET /api/v1/app/events/:kind`. */
export interface EventTimes {
  readonly startMs: number;
  /** Stimato dallo scraper: `start + MATCH_MINUTES`. Non è un fatto. */
  readonly endMs: number;
  /** Dalla guida EPG. Assente quando la guida non ha trovato il programma. */
  readonly epgStartMs?: number | null;
  readonly epgEndMs?: number | null;
}

/** La finestra su cui si dà il verdetto, e da dove viene. */
export function resolveLivenessWindow(times: EventTimes): TimeWindow & { source: LivenessSource } {
  const { epgStartMs, epgEndMs } = times;
  if (typeof epgStartMs === 'number' && typeof epgEndMs === 'number') {
    return { startMs: epgStartMs, endMs: epgEndMs, source: 'epg' };
  }
  return { startMs: times.startMs, endMs: times.endMs, source: 'estimated' };
}

/**
 * Verdetto su una finestra qualunque. Inizio incluso, fine esclusa — la stessa
 * convenzione del prototipo v3 (`o.s <= NOW && NOW < o.e`).
 */
export function livenessOf(span: TimeWindow, serverNowMs: number): Liveness {
  if (serverNowMs < span.startMs) return 'future';
  if (serverNowMs >= span.endMs) return 'past';
  return 'live';
}

/** Verdetto su un evento sportivo, con la provenienza della finestra usata. */
export function eventLiveness(times: EventTimes, serverNowMs: number): LivenessVerdict {
  const span = resolveLivenessWindow(times);
  return { state: livenessOf(span, serverNowMs), source: span.source };
}

/**
 * Quanto è avanzato, da 0 a 100. Zero fuori dalla finestra: il prototipo mostra la barra
 * solo sul programma in onda (`o.pct = o.live ? … : 0`).
 */
export function progressPercent(span: TimeWindow, serverNowMs: number): number {
  if (livenessOf(span, serverNowMs) !== 'live') return 0;
  const duration = span.endMs - span.startMs;
  if (duration <= 0) return 0;
  return Math.round(((serverNowMs - span.startMs) / duration) * 100);
}
